import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { categoryLabels, listingLabels } from '../../utils/helpers';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Calendar, Users, MousePointer, TrendingUp, Filter, Info, Copy, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Minus, Lightbulb } from 'lucide-react';

interface AnalyticsSummary {
  totalEvents: number;
  totalSessions: number;
  totalConversions: number;
  byDate: Record<string, number>;
  dateSeries: string[];
  byCampaignDate: Record<string, Record<string, number>>;
  bySource: Record<string, number>;
  byPath: Record<string, number>;
  campaignComparison: {
    name: string;
    pageviews: number;
    sessions: number;
    conversions: number;
    conversionRate: number;
  }[];
}

interface AnalyticsDashboardProps {
  token: string;
  apiBase: string;
}

const PRESETS = [
  { label: '最近 7 天', days: 7 },
  { label: '最近 30 天', days: 30 },
  { label: '最近 90 天', days: 90 },
];

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function subtractDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatDate(d);
}

// 廣告活動名稱人話化：meta_202608_ad_a → 「Meta 廣告 · 2026 年 8 月 · 版本 A」
function humanizeCampaign(raw: string): string {
  const platformMap: Record<string, string> = {
    meta: 'Meta 廣告',
    fb: 'Facebook 廣告',
    facebook: 'Facebook 廣告',
    ig: 'IG 廣告',
    instagram: 'IG 廣告',
    google: 'Google 廣告',
    tiktok: 'TikTok 廣告',
  };
  const out: string[] = [];
  for (const part of raw.split('_').filter(Boolean)) {
    const lower = part.toLowerCase();
    if (/^\d{6}$/.test(lower)) {
      out.push(`${lower.slice(0, 4)} 年 ${parseInt(lower.slice(4), 10)} 月`);
    } else if (platformMap[lower]) {
      out.push(platformMap[lower]);
    } else if (/^ad[-_]?\w?$/.test(lower) || ['a', 'b', 'c'].includes(lower)) {
      out.push(`版本 ${lower.replace(/^ad[-_]?/, '').toUpperCase()}`);
    } else if (lower === 'paid') {
      out.push('付費');
    } else if (lower === 'organic') {
      out.push('自然流量');
    } else if (lower === 'post' || lower === 'boost') {
      out.push('貼文推廣');
    } else {
      out.push(part);
    }
  }
  return out.join(' · ') || raw;
}

// 對比徽章：與上一期比較
const DeltaBadge: React.FC<{ cur: number; prev: number; goodWhenUp?: boolean }> = ({
  cur,
  prev,
  goodWhenUp = true,
}) => {
  if (prev === 0) {
    return cur > 0 ? (
      <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-green-100 px-1.5 py-0.5 text-xs font-bold text-green-700">
        <ArrowUpRight className="h-3 w-3" /> 新增
      </span>
    ) : null;
  }
  if (cur === prev) {
    return (
      <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-bold text-slate-500">
        <Minus className="h-3 w-3" /> 持平
      </span>
    );
  }
  const up = cur > prev;
  const good = up === goodWhenUp;
  return (
    <span
      className={`ml-1 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-bold ${
        good ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(Number((((cur - prev) / prev) * 100).toFixed(1)))}%
    </span>
  );
};

const BigStat: React.FC<{
  icon: React.ReactNode;
  label: string;
  cur: number;
  prev: number | null;
  suffix?: string;
}> = ({ icon, label, cur, prev, suffix = '' }) => (
  <div className="rounded-xl bg-white p-5 shadow-sm">
    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-500">
      {icon}
      {label}
    </div>
    <p className="text-3xl font-bold text-slate-900">
      {cur.toLocaleString()}
      {suffix}
    </p>
    <div className="mt-1.5 text-xs text-slate-400">
      對上同期：{prev === null ? '—' : `${prev.toLocaleString()}${suffix}`}
      {prev !== null && <DeltaBadge cur={cur} prev={prev} />}
    </div>
  </div>
);

// 迷你趨勢線（純 SVG，輕量）
const Spark: React.FC<{ series: number[] }> = ({ series }) => {
  const max = Math.max(...series, 0);
  if (series.length < 2 || max === 0) return <div className="h-7" />;
  const pts = series
    .map((v, i) => `${(i / (series.length - 1)) * 100},${28 - (v / max) * 24}`)
    .join(' ');
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="h-7 w-full">
      <polyline points={pts} fill="none" stroke="#2563eb" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};


const AnalyticsGuide: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('facebook');
  const [medium, setMedium] = useState('paid');
  const [campaign, setCampaign] = useState('meta_2026xx');
  const [content, setContent] = useState('');
  const [copied, setCopied] = useState(false);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://marveland.hk';
  const params = new URLSearchParams();
  if (source) params.set('utm_source', source);
  if (medium) params.set('utm_medium', medium);
  if (campaign) params.set('utm_campaign', campaign);
  if (content) params.set('utm_content', content);
  const query = params.toString();
  const trackingUrl = `${baseUrl}/${query ? `?${query}` : ''}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(trackingUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 text-slate-900">
          <Info className="h-5 w-5 text-blue-800" />
          <h3 className="text-lg font-bold">Analytics 使用指南（如何追蹤 FB 廣告？）</h3>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
      </button>

      {open && (
        <div className="mt-4 space-y-6 text-sm text-slate-700">
          <div className="rounded-lg bg-blue-50 p-4">
            <p className="mb-3 font-bold text-blue-900">如何追蹤 Facebook 廣告成效？（3 分鐘搞定）</p>
            <ol className="list-inside list-decimal space-y-3">
              <li>
                <strong>在下面產生你的廣告連結</strong>
                <p className="mt-1 text-slate-600">
                  在「廣告活動名稱」欄位輸入一個容易認的名字，例如{' '}
                  <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">meta_august_ad_a</code>，然後按「複製」。
                </p>
              </li>
              <li>
                <strong>打開 Facebook Ads Manager</strong>
                <p className="mt-1 text-slate-600">進入你要設定追蹤的「廣告」（Ad level，不是廣告組合）。</p>
              </li>
              <li>
                <strong>貼到「網站網址」欄位</strong>
                <p className="mt-1 text-slate-600">
                  找到 <span className="font-semibold text-slate-800">Website URL / 網站網址</span>，把剛剛複製的完整連結貼上去，例如：
                </p>
                <p className="mt-1 break-all rounded bg-white p-2 font-mono text-xs text-slate-600">
                  https://marveland.hk/?utm_source=facebook&utm_medium=paid&utm_campaign=meta_august_ad_a
                </p>
              </li>
              <li>
                <strong>不同廣告用不同名字</strong>
                <p className="mt-1 text-slate-600">
                  廣告 A 用{' '}
                  <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">meta_august_ad_a</code>，廣告 B 用{' '}
                  <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">meta_august_ad_b</code>。這樣 Dashboard 才能分開比較。
                </p>
              </li>
              <li>
                <strong>回到 Analytics Dashboard 看結果</strong>
                <p className="mt-1 text-slate-600">廣告開始投放後，本頁會自動出現對應的瀏覽量、查詢次數和轉換率。</p>
              </li>
            </ol>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="mb-3 font-bold">步驟 1：產生廣告追蹤連結</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold text-slate-500">來源（通常填 facebook）</label>
                <input
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="facebook"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">媒介（通常填 paid）</label>
                <input
                  value={medium}
                  onChange={(e) => setMedium(e.target.value)}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="paid"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-500">廣告活動名稱（必填，建議：meta_年份月份_廣告編號）</label>
                <input
                  value={campaign}
                  onChange={(e) => setCampaign(e.target.value)}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="meta_2026xx"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-500">廣告版本（可選，例如 ad_a / ad_b）</label>
                <input
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="ad_a"
                />
              </div>
            </div>
            <div className="mt-3 flex items-start gap-2">
              <p className="flex-1 break-all rounded bg-white p-2 font-mono text-xs text-slate-700">{trackingUrl}</p>
              <button
                onClick={copyToClipboard}
                className="flex shrink-0 items-center gap-1 rounded bg-blue-800 px-3 py-2 text-xs font-bold text-white hover:bg-blue-900"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied ? '已複製' : '複製連結'}
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg bg-gold-50 p-4">
              <p className="mb-2 font-bold text-gold-900">什麼是「查詢」？</p>
              <p>查詢 = 客人做了對生意有幫助的動作。系統會自動追蹤：</p>
              <ul className="mt-1.5 list-inside list-disc space-y-1">
                <li>點擊「WhatsApp 聯絡」按鈕</li>
                <li>提交「業主放盤」表單</li>
              </ul>
              <p className="mt-2">查詢次數越多，代表廣告帶來的潛在客戶越多。</p>
            </div>
            <div className="rounded-lg bg-green-50 p-4">
              <p className="mb-2 font-bold text-green-900">什麼是「廣告活動」？</p>
              <p>
                廣告活動就是你在 Facebook 設定的{' '}
                <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">utm_campaign</code> 名稱。
              </p>
              <p className="mt-2">系統會自動把帶有相同名稱的訪客歸在同一組，讓你知道哪個廣告最有效。</p>
              <p className="mt-2 text-green-800">沒有帶 utm_campaign 的流量會顯示為 (direct)。</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ token, apiBase }) => {
  const [start, setStart] = useState(subtractDays(7));
  const [end, setEnd] = useState(formatDate(new Date()));
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [prev, setPrev] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [propStats, setPropStats] = useState<
    { id: string; title: string; code: string; views: number; fbClicks: number }[]
  >([]);
  // 現存物業索引（刪除/不存在的盤源數據一律不顯示）
  const [propIndex, setPropIndex] = useState<
    Record<string, { title: string; code: string; category: string; listing: string }>
  >({});

  useEffect(() => {
    fetchSummary();
    fetchPropStats();
  }, [start, end, token, apiBase]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      // 同時抓「上一個等長区間」做對比（老闆視角：數字必須有對比才有意義）
      const [sy, sm, sd] = start.split('-').map(Number);
      const [ey, em, ed] = end.split('-').map(Number);
      const startD = new Date(sy, sm - 1, sd);
      const endD = new Date(ey, em - 1, ed);
      const span = Math.max(1, Math.round((endD.getTime() - startD.getTime()) / 86400000) + 1);
      const prevEndD = new Date(startD);
      prevEndD.setDate(prevEndD.getDate() - 1);
      const prevStartD = new Date(prevEndD);
      prevStartD.setDate(prevStartD.getDate() - (span - 1));
      const prevStart = formatDate(prevStartD);
      const prevEnd = formatDate(prevEndD);

      const qs = new URLSearchParams({ start, end });
      const [curRes, prevRes] = await Promise.all([
        fetch(`${apiBase}/api/analytics?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/api/analytics?start=${prevStart}&end=${prevEnd}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (curRes.ok) setData(await curRes.json());
      if (prevRes.ok) setPrev(await prevRes.json());
    } finally {
      setLoading(false);
    }
  };

  const fetchPropStats = async () => {
    try {
      const [statsRes, cmsRes] = await Promise.all([
        fetch(`${apiBase}/api/analytics/properties`),
        fetch(`${apiBase}/api/cms/data`),
      ]);
      const stats = statsRes.ok
        ? ((await statsRes.json()) as Record<string, { views: number; fbClicks: number }>)
        : {};
      const cms = cmsRes.ok ? await cmsRes.json() : { properties: [] };
      const cmsProps = (cms.properties || []) as Array<Record<string, any>>;
      const index: Record<string, { title: string; code: string; category: string; listing: string }> = {};
      for (const pr of cmsProps)
        index[pr.id] = {
          title: pr.title || '',
          code: pr.code || '',
          category: categoryLabels[pr.category as keyof typeof categoryLabels] || '',
          listing: listingLabels[pr.listingType as keyof typeof listingLabels] || '',
        };
      setPropIndex(index);
      const rows = Object.entries(stats)
        .filter(([id]) => !!index[id]) // 已刪除/不存在物業的數據不顯示
        .map(([id, s]) => ({
          id,
          title: index[id].title || id,
          code: index[id].code,
          views: s.views || 0,
          fbClicks: s.fbClicks || 0,
        }));
      setPropStats(rows);
    } catch {
      // 非必要數據，失敗不影響主圖表
    }
  };

  const conversionRate = useMemo(() => {
    if (!data || data.totalEvents === 0) return 0;
    return Number(((data.totalConversions / data.totalEvents) * 100).toFixed(2));
  }, [data]);

  const prevConversionRate = useMemo(() => {
    if (!prev || prev.totalEvents === 0) return 0;
    return Number(((prev.totalConversions / prev.totalEvents) * 100).toFixed(2));
  }, [prev]);

  const lineData = useMemo(() => {
    if (!data) return [];
    return data.dateSeries.map((date) => ({ date, total: data.byDate[date] || 0 }));
  }, [data]);

  const sourceData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.bySource)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [data]);

  // 熱門頁面：路徑翻譯成人話（物業頁顯示樓盤編號，其餘顯示頁面名稱）
  const pathData = useMemo(() => {
    if (!data) return [];
    const label = (path: string): string => {
      const m = /\/properties\/([A-Za-z0-9_-]+)/.exec(path);
      if (m) {
        const prop = propIndex[m[1]];
        return prop ? `${prop.code}` : m[1];
      }
      const known: Record<string, string> = {
        '/': '首頁',
        '/properties': '物業列表',
        '/about': '關於我們',
        '/contact': '聯絡我們',
        '/owner-listing': '業主放盤',
        '/privacy': '私隱政策',
      };
      return known[path] || path;
    };
    return Object.entries(data.byPath)
      .map(([path, value]) => ({ name: label(path), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [data, propIndex]);

  // 廣告活動卡片：按查詢數排序（查詢相同比瀏覽量）
  const campaignCards = useMemo(() => {
    if (!data) return [];
    return [...data.campaignComparison]
      .filter((c) => c.name === '(direct)' || !!propIndex[c.name])
      .sort((a, b) => b.conversions - a.conversions || b.pageviews - a.pageviews);
  }, [data]);

  // 卡片分頁：每頁 15 個
  const CARD_PAGE_SIZE = 15;
  const [cardPage, setCardPage] = useState(1);
  useEffect(() => {
    setCardPage(1);
  }, [data]);
  const cardTotalPages = Math.max(1, Math.ceil(campaignCards.length / CARD_PAGE_SIZE));
  const cardSafePage = Math.min(cardPage, cardTotalPages);
  const pagedCards = useMemo(
    () => campaignCards.slice((cardSafePage - 1) * CARD_PAGE_SIZE, cardSafePage * CARD_PAGE_SIZE),
    [campaignCards, cardSafePage]
  );
  const goCardPage = (pg: number) => {
    setCardPage(pg);
    window.scrollTo({ top: 240, behavior: 'smooth' });
  };

  const campaignSeries = (name: string): number[] => {
    if (!data) return [];
    return data.dateSeries.map((d) => data.byCampaignDate[name]?.[d] || 0);
  };

  const propRanking = useMemo(
    () =>
      [...propStats]
        .filter((r) => r.views > 0 || r.fbClicks > 0)
        .sort((a, b) => b.fbClicks - a.fbClicks || b.views - a.views)
        .slice(0, 5),
    [propStats]
  );

  const topProp = propRanking[0];

  // 自動建議（規則式）
  const suggestions = useMemo(() => {
    const out: string[] = [];
    const best = campaignCards[0];
    if (best && best.conversions > 0) {
      out.push(
        `「${humanizeCampaign(best.name)}」表現最好，帶來 ${best.conversions} 個查詢——建議加碼投放同類廣告。`
      );
    }
    campaignCards
      .filter((c) => c.pageviews >= 20 && c.conversions === 0)
      .slice(0, 2)
      .forEach((c) => {
        out.push(
          `「${humanizeCampaign(c.name)}」有 ${c.pageviews} 次瀏覽但零查詢——建議換封面圖或調整文案後重發。`
        );
      });
    if (topProp) {
      out.push(
        `「${topProp.title}」人氣最高（FB 點擊 ${topProp.fbClicks}、瀏覽 ${topProp.views}）——建議保持更新，並考慮優先投放。`
      );
    }
    if (out.length === 0) {
      out.push('暫時未有足夠數據。先到物業編輯頁的「社群推廣」發出第一則貼文，或用下方連結產生器開始廣告追蹤。');
    }
    return out;
  }, [campaignCards, topProp]);

  return (
    <div className="space-y-6">
      <AnalyticsGuide />

      {/* 日期與活動選擇 */}
      <div className="rounded-xl bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          {PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => {
                setStart(subtractDays(p.days));
                setEnd(formatDate(new Date()));
              }}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-500">開始日期</label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-500">結束日期</label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">載入中...</p>}

      {data && (
        <>
          {/* ① 一分鐘看完（三句話總結） */}
          <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-slate-900">
              <Info className="h-5 w-5 text-blue-700" /> 一分鐘看完
            </h3>
            <div className="space-y-2 text-[15px] leading-relaxed text-slate-800">
              <p>
                📊 這段時間網站共有 <strong>{data.totalEvents.toLocaleString()}</strong> 次瀏覽、
                <strong> {data.totalSessions.toLocaleString()}</strong> 位訪客。
              </p>
              <p>
                💬 帶來 <strong>{data.totalConversions.toLocaleString()}</strong> 個查詢
                {prev && (prev.totalConversions > 0 || data.totalConversions > 0) && (
                  <DeltaBadge cur={data.totalConversions} prev={prev.totalConversions} />
                )}
                ，轉換率 {conversionRate}%。
              </p>
              <p>
                🏠{' '}
                {topProp ? (
                  <>
                    最受歡迎的盤源是「<strong>{topProp.title}</strong>」（FB 點擊 {topProp.fbClicks}、瀏覽 {topProp.views}）。
                  </>
                ) : (
                  '盤源熱度統計會在貼文開始帶來流量後出現。'
                )}
              </p>
            </div>
          </div>

          {/* ② 表現大卡＋趨勢箭頭 */}
          <div className="grid gap-4 sm:grid-cols-3">
            <BigStat
              icon={<MousePointer className="h-4 w-4 text-blue-800" />}
              label="總瀏覽量"
              cur={data.totalEvents}
              prev={prev?.totalEvents ?? null}
            />
            <BigStat
              icon={<Users className="h-4 w-4 text-green-600" />}
              label="查詢次數"
              cur={data.totalConversions}
              prev={prev?.totalConversions ?? null}
            />
            <BigStat
              icon={<TrendingUp className="h-4 w-4 text-purple-600" />}
              label="轉換率"
              cur={conversionRate}
              prev={prevConversionRate}
              suffix="%"
            />
          </div>

          {/* ③ 廣告活動卡片制 */}
          <div>
            <h3 className="mb-4 text-lg font-bold text-slate-900">
              物業表現（按查詢數排序，每頁 {CARD_PAGE_SIZE} 個
              {campaignCards.length > 0 && <>，共 {campaignCards.length} 個</>}）
            </h3>
            {campaignCards.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500">
                還沒有廣告活動數據。用下方「Analytics 使用指南」產生追蹤連結開始投放。
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {pagedCards.map((c, i) => (
                  <div
                    key={c.name}
                    className={`relative rounded-xl border p-5 shadow-sm ${
                      i === 0 && c.conversions > 0
                        ? 'border-gold-600 bg-gradient-to-b from-gold-50 to-white'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    {i === 0 && c.conversions > 0 && (
                      <span className="absolute right-3 top-3 rounded-full bg-gold-600 px-2 py-0.5 text-xs font-bold text-white">
                        🏆 表現最佳
                      </span>
                    )}
                    {(() => {
                      const prop = propIndex[c.name];
                      if (prop) {
                        return (
                          <>
                            <div className="mb-1 flex flex-wrap items-center gap-2 pr-16">
                              <Link
                                to={`/properties/${c.name}`}
                                className="text-base font-black text-blue-700 underline decoration-blue-300 hover:text-blue-900"
                              >
                                {prop.code}
                              </Link>
                              {prop.category && (
                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600">
                                  {prop.category}
                                </span>
                              )}
                              {prop.listing && (
                                <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-blue-700">
                                  {prop.listing}
                                </span>
                              )}
                            </div>
                            <p className="mb-1 break-words pr-16 text-xs text-slate-500">{prop.title}</p>
                          </>
                        );
                      }
                      return (
                        <p className="mb-1 break-words pr-16 text-sm font-bold text-slate-800">
                          {c.name === '(direct)' ? '直接流量（沒有帶廣告追蹤）' : humanizeCampaign(c.name)}
                        </p>
                      );
                    })()}
                    <p className="mt-3 text-3xl font-black text-blue-800">
                      {c.conversions.toLocaleString()}
                      <span className="ml-1 text-sm font-semibold text-slate-500">個查詢</span>
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      瀏覽 {c.pageviews.toLocaleString()} 次 · 轉換率 {c.conversionRate}%
                    </p>
                    <div className="mt-2">
                      <Spark series={campaignSeries(c.name)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 卡片分頁導航 */}
          {cardTotalPages > 1 && (
            <nav className="flex flex-wrap items-center justify-center gap-2" aria-label="分頁">
              <button
                type="button"
                onClick={() => goCardPage(cardSafePage - 1)}
                disabled={cardSafePage === 1}
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-600 hover:border-blue-800 hover:text-blue-800 disabled:opacity-40"
              >
                ← 上一頁
              </button>
              {Array.from({ length: cardTotalPages }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  type="button"
                  onClick={() => goCardPage(pg)}
                  className={`h-9 w-9 rounded-lg text-sm font-bold ${
                    pg === cardSafePage ? 'bg-blue-800 text-white' : 'border border-slate-300 text-slate-600 hover:border-blue-800'
                  }`}
                >
                  {pg}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goCardPage(cardSafePage + 1)}
                disabled={cardSafePage === cardTotalPages}
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-600 hover:border-blue-800 hover:text-blue-800 disabled:opacity-40"
              >
                下一頁 →
              </button>
            </nav>
          )}

          {/* ④ 盤源熱度排行 */}
          <div className="rounded-xl bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-lg font-bold text-slate-900">盤源熱度排行 Top 5</h3>
            {propRanking.length === 0 ? (
              <p className="text-sm text-slate-500">還沒有流量數據——發出貼文後這裡會顯示哪個盤最搶手。</p>
            ) : (
              <ol className="space-y-2">
                {propRanking.map((r, i) => (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 px-4 py-2.5"
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-black ${
                        i === 0 ? 'bg-gold-600 text-white' : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                      <Link to={`/properties/${r.id}`} className="text-slate-800 underline decoration-slate-300 hover:text-blue-700">
                        {r.title}
                      </Link>{' '}
                      <span className="text-xs font-normal text-slate-400">({r.code})</span>
                    </span>
                    <span className="shrink-0 text-xs">
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 font-bold text-blue-700">↗ {r.fbClicks}</span>
                      <span className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 font-bold text-slate-600">👁 {r.views}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* ⑤ 下一步建議 */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-amber-900">
              <Lightbulb className="h-5 w-5 text-amber-600" /> 下一步建議
            </h3>
            <ul className="list-inside list-disc space-y-2 text-sm leading-relaxed text-amber-900">
              {suggestions.map((sg, i) => (
                <li key={i}>{sg}</li>
              ))}
            </ul>
          </div>

          {/* 每日趨勢（總計單線） */}
          <div className="rounded-xl bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-lg font-bold text-slate-900">每日瀏覽量趨勢</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2} dot={false} name="瀏覽量" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 進階圖表 */}
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-bold text-slate-900">流量來源</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sourceData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-bold text-slate-900">熱門頁面</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pathData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#C5A028" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  );
};
